import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { useProductsActions } from 'datastage/store/products/useProducts';
import { datastageApi } from 'datastage/api';
import { filterCriteria } from 'datastage/utils/filterCriteria';

// Minimum time to show the loading spinner (in ms) for consistent UX
const MIN_LOADING_DURATION = 400;

/**
 * Custom hook that consolidates products data fetching logic.
 * Handles loading states with minimum duration, error toasts, and Redux updates.
 *
 * This pattern ensures:
 * - Consistent loading spinner behavior (minimum 400ms display)
 * - Centralized error handling
 * - Reusable fetch logic across components
 *
 * @returns {Object} Object containing fetchProducts function and products state values
 */
export function useProductsData() {
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast } = useToasts();
    const { updateProducts } = useProductsActions();
    const { filterForm, productRange, productSort, itemsPerPage } = useSelector(
        (s) => s.products,
    );

    /**
     * Fetches products from the API with the given range.
     * Ensures loading spinner is visible for at least MIN_LOADING_DURATION.
     *
     * @param {Array} range - The pagination range array (e.g., [0, 19])
     * @param {Object|null} customFilterData - Optional filter data to override Redux filterForm
     * @param {Array|null} customSort - Optional sort array (e.g., ["id", "ASC"]) to override Redux productSort
     * @returns {Promise<Object|null>} The API response data or null on error
     */
    const fetchProducts = useCallback(
        async (range, customFilterData = null, customSort = null) => {
            const startTime = Date.now();
            setLoadingTrue();

            const filterData = customFilterData ?? filterForm;
            const sortData = customSort ?? productSort;

            try {
                const data = await datastageApi.products(
                    filterCriteria(filterData, '', 'products'),
                    range,
                    sortData,
                );

                if (data?.code) {
                    showErrorToast(data?.message || 'Failed to fetch products.');
                    return null;
                }

                return data;
            } catch (error) {
                showErrorToast(error?.message || 'Error fetching products.');
                console.error('Error fetching products:', error);
                return null;
            } finally {
                // Ensure minimum loading duration for consistent UX
                const elapsed = Date.now() - startTime;
                const remainingTime = MIN_LOADING_DURATION - elapsed;

                if (remainingTime > 0) {
                    setTimeout(() => setLoadingFalse(), remainingTime);
                } else {
                    setLoadingFalse();
                }
            }
        },
        [filterForm, productSort, setLoadingTrue, setLoadingFalse, showErrorToast],
    );

    return {
        fetchProducts,
        updateProducts,
        itemsPerPage,
        productRange,
        filterForm,
        productSort,
    };
}
